/**
 * 新着通知ポップアップ + 通知音フック (管理画面を開いている間だけ有効)。
 *
 * 仕様:
 * - 既存 GET /admin/notifications を軽量ポーリング (POLL_INTERVAL_MS) で再利用する
 *   (新規 BE / API 追加なし)。
 * - 初回取得は baseline として全 notifId を記録するだけで、既存通知を大量ポップアップしない。
 * - 以降に増えた notifId だけをポップアップ表示する (同一 ID は重複表示しない)。
 * - caseId を持つ通知はポップアップ本文クリックで案件ページ (`/cases/{caseId}?tab=message`)
 *   へ遷移する (QA-093 系。NotificationDrawer と同一遷移先)。caseId が無い通知は従来どおり。
 * - 表示可否 (popup / sound) は **サーバーのユーザー個人設定** を参照する
 *   (GET /admin/users/me/preferences → notifications.inAppPopup / inAppSound)。
 *   mount 時に 1 回取得し、PreferencesPage 保存時の `PREFS_CHANGED_EVENT` で即時更新する。
 * - ポーリングはタブが hidden でも継続する (通知改善 B: 別タブ中も音で気づけるようにする)。
 *   hidden 中は toast を出さず通知音のみ試行し、検知済み (seen) に積む。visible 復帰時に
 *   過去分 toast をまとめて再表示はしない (未読確認は既存ベル / ドロワーに任せる)。
 *   なお hidden 中の setInterval はブラウザのタイマー制限 (Chrome intensive throttling:
 *   hidden 5 分超で約 1 回/分) により自然に間引かれるため、過剰 polling にはならない。
 * - 通知音は inAppSound=true なら inAppPopup の ON/OFF に関係なく鳴らす。
 * - API エラーは握り潰してトーストを連発しない。表示失敗しても業務操作を止めない。
 *
 * 描画を持たないため AppLayout 配下 (認証済み + ToastProvider 内) にマウントして使う。
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiFetch } from '@/api/client';
import type { Notification } from '@/types';
import {
  detectNewNotifications,
  notificationSeenKey,
  buildPopupItems,
  decidePopupActions,
} from '@/utils/notificationPopup';
import {
  parseInAppNotificationPreferences,
  DEFAULT_IN_APP_NOTIFICATION_PREFERENCES,
  type InAppNotificationPreferences,
} from '@/utils/notificationPreferences';
import { playNotificationChime } from '@/utils/notificationSound';
import { useLocale } from '@/i18n';

/** ポーリング間隔 (ms)。既存 unread-count (30s) より長めに設定し過剰負荷を避ける。 */
const POLL_INTERVAL_MS = 45_000;
/** ポップアップ (トースト) の表示時間 (ms)。 */
const POPUP_DURATION_MS = 6000;

/**
 * PreferencesPage がアプリ内通知設定を保存したときに発火するイベント名。
 * 保存内容を即時にこのフックへ反映するために使う (再フェッチ不要)。
 */
export const PREFS_CHANGED_EVENT = 'acecollect:notification-prefs-changed';

export function useNotificationPopup(): void {
  const { user } = useAuth();
  const { showToast } = useToast();
  // ポップアップの枠の文言 (新着通知 / メッセージを確認 など) だけ表示言語に合わせる。
  // ポーリングの effect を貼り直さないよう ref で最新の言語を読む。
  const { locale } = useLocale();
  const localeRef = useRef(locale);
  localeRef.current = locale;

  const orgId = user?.orgId ?? '';
  const userId = user?.userId ?? '';

  // showToast の参照変化で effect を貼り直さない (ポーリングを止めない) ための ref。
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useEffect(() => {
    // 未認証 (orgId/userId 不明) の間は何もしない。
    if (!orgId || !userId) return;

    const seen = new Set<string>();
    let baselineEstablished = false;
    let disposed = false;
    // 表示可否 (サーバー個人設定)。取得できるまでは default を使う。
    let prefs: InAppNotificationPreferences = DEFAULT_IN_APP_NOTIFICATION_PREFERENCES;

    // サーバーのユーザー個人設定を取得して popup/sound を反映 (失敗時は現状維持)。
    const loadPrefs = async (): Promise<void> => {
      try {
        const res = await apiFetch<{ preferences?: { notifications?: Record<string, unknown> } }>(
          '/admin/users/me/preferences',
        );
        if (!disposed) prefs = parseInAppNotificationPreferences(res.preferences?.notifications);
      } catch {
        /* 取得失敗時は default / 既存値のまま */
      }
    };
    void loadPrefs();

    // PreferencesPage の保存時に即時反映 (detail があれば再フェッチ不要)。
    const onPrefsChanged = (event: Event): void => {
      const detail = (event as CustomEvent).detail as Partial<InAppNotificationPreferences> | null;
      if (
        detail &&
        typeof detail.inAppPopup === 'boolean' &&
        typeof detail.inAppSound === 'boolean'
      ) {
        prefs = { inAppPopup: detail.inAppPopup, inAppSound: detail.inAppSound };
      } else {
        void loadPrefs();
      }
    };
    window.addEventListener(PREFS_CHANGED_EVENT, onPrefsChanged);

    const poll = async (): Promise<void> => {
      // 通知改善 B: hidden でも取得は継続する (別タブ中も音で気づけるようにするため)。
      // hidden 中の取得頻度はブラウザのタイマー制限で自然に間引かれる (ヘッダコメント参照)。
      let items: Notification[];
      try {
        const res = await apiFetch<{ items: Notification[] }>('/admin/notifications');
        items = res.items ?? [];
      } catch {
        // ネットワーク / 認証エラー時はトーストを出さず次回ポーリングに任せる。
        return;
      }
      if (disposed) return;

      // 初回成功時は baseline 記録のみ (既存通知を大量表示しない)。
      if (!baselineEstablished) {
        for (const n of items) if (n.notifId) seen.add(notificationSeenKey(n));
        baselineEstablished = true;
        return;
      }

      const fresh = detectNewNotifications(items, seen);
      // 表示/再生の有無に関わらず seen に積む = 後で設定 ON にしても過去分が湧かない。
      // hidden 中に検知した分も消費済み扱いとし、visible 復帰時にまとめて toast 再表示しない。
      // キーは notificationSeenKey が決める — 既定は notifId、メッセージ集約だけ版つき
      // (グループが伸びた回のみ再検知され、同じ版の再 poll では二重表示しない)。
      for (const n of fresh) seen.add(notificationSeenKey(n));

      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const actions = decidePopupActions({ freshCount: fresh.length, hidden, prefs });

      if (actions.showToasts) {
        // caseId を持つ通知はトースト本文クリックで案件ページへ遷移 (QA-093 系)。
        // 新着メッセージは buildPopupItems が title / preview / actionLabel を返すので、
        // それを structured 引数へそのまま渡して 2 段表示にする
        // (notification-banner-readability-20260820)。集約表示や他種別は title を
        // 持たないため structured は undefined になり、従来の 1 行トーストのまま。
        for (const item of buildPopupItems(fresh, undefined, localeRef.current)) {
          showToastRef.current(
            'info',
            item.message,
            POPUP_DURATION_MS,
            item.targetPath ?? undefined,
            item.title
              ? { title: item.title, preview: item.preview, actionLabel: item.actionLabel }
              : undefined,
          );
        }
      }
      if (actions.playSound) {
        // 鳴らせなくても (autoplay block 等) 例外は出さない (理由は notificationSound 側で warn)。
        playNotificationChime();
      }
    };

    // 初回 baseline 取得 → 以降ポーリング。
    void poll();
    const intervalId = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    // hidden → visible 復帰時に即時再取得 (離席中に届いた分を拾う)。
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(PREFS_CHANGED_EVENT, onPrefsChanged);
    };
  }, [orgId, userId]);
}
